import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Avatar,
  Dialog,
  DialogContent,
  Box,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Download, Maximize2, X as CloseIcon } from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import { exportToExcel } from "../../utils/util";

const CountrywiseRegistrationsCard2 = () => {
  const { data: countrywiseData, isLoading: isCountrywiseLoading } = useApiEp(
    `/user/get-countrywise-registrations`
  );
  const [isFullscreen, setFullscreen] = useState(false);

  const defaultMockCountries = [
    { country: "India", count: 2364, flag: "https://flagcdn.com/w40/in.png" },
    { country: "Russia", count: 5, flag: "https://flagcdn.com/w40/ru.png" },
    { country: "United Arab Emirates", count: 3, flag: "https://flagcdn.com/w40/ae.png" },
    { country: "China", count: 1, flag: "https://flagcdn.com/w40/cn.png" },
    { country: "United States", count: 1, flag: "https://flagcdn.com/w40/us.png" },
    { country: "Nepal", count: 1, flag: "https://flagcdn.com/w40/np.png" },
    { country: "Turkey", count: 1, flag: "https://flagcdn.com/w40/tr.png" },
    { country: "Indonesia", count: 1, flag: "https://flagcdn.com/w40/id.png" },
    { country: "Taiwan", count: 1, flag: "https://flagcdn.com/w40/tw.png" },
  ];

  const dataByCountry = useMemo(() => {
    if (!countrywiseData || !Array.isArray(countrywiseData.registrations)) {
      return defaultMockCountries;
    }

    const filtered = countrywiseData.registrations
      .filter((item) => item.country)
      .map((item) => ({
        country: item.country,
        count: item.count,
        flag:
          item.countryDetails?.flag ||
          `https://flagcdn.com/w40/${item.country.substring(0, 2).toLowerCase()}.png`,
      }));

    return filtered.length > 0 ? filtered : defaultMockCountries;
  }, [countrywiseData]);

  const handleFullscreenToggle = () => setFullscreen((prev) => !prev);

  const RenderYAxisTick = (props) => {
    const { x, y, payload } = props;
    const country = payload.value;
    const item = dataByCountry.find((c) => c.country === country);

    return (
      <g transform={`translate(${x},${y})`}>
        <foreignObject x={-155} y={-14} width={150} height={28}>
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
            {item?.flag ? (
              <Avatar src={item.flag} alt={country} sx={{ width: 18, height: 18 }} />
            ) : null}
            <Typography
              variant="body2"
              noWrap
              sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1E293B" }}
            >
              {country}
            </Typography>
          </Stack>
        </foreignObject>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E7EAF0",
            boxShadow: "0px 4px 20px rgba(16, 24, 40, 0.1)",
            borderRadius: "10px",
            p: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {data.flag && <Avatar src={data.flag} alt={data.country} sx={{ width: 20, height: 20 }} />}
            <Typography variant="subtitle2" fontWeight="700" color="#1E293B">
              {data.country}
            </Typography>
          </Stack>
          <Typography variant="body2" color="#10B981" fontWeight="700" sx={{ mt: 0.5 }}>
            Registrations: {data.count?.toLocaleString()}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (isCountrywiseLoading) {
    return (
      <Card sx={{ height: 380, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Card>
    );
  }

  const renderChart = (isModal = false) => {
    const dataList = isModal ? dataByCountry : dataByCountry.slice(0, 5);
    // Dynamic height calculation ensures clean vertical line spacing for every country bar
    const chartHeight = isModal
      ? Math.max(380, dataList.length * 52)
      : Math.max(260, dataList.length * 48);

    return (
      <Box sx={{ width: "100%", height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={dataList}
            margin={{ top: 15, right: 40, left: 145, bottom: 20 }}
            barCategoryGap={20}
          >
            <XAxis type="number" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="country"
              tick={<RenderYAxisTick />}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" fill="#10B981" radius={[0, 6, 6, 0]} barSize={16}>
              <LabelList
                dataKey="count"
                position="right"
                style={{ fill: "#1E293B", fontWeight: 600, fontSize: 12 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  return (
    <>
      <Card sx={{ height: 380, display: "flex", flexDirection: "column", borderRadius: "14px", p: 1 }}>
        <CardContent sx={{ pb: 1, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" fontWeight="700" color="#1E293B" sx={{ fontSize: "1.05rem" }}>
              Registrations by Country
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={() => exportToExcel(dataByCountry, "Registrations_by_Country")}
                sx={{
                  borderColor: "#E2E8F0",
                  color: "#475467",
                  borderRadius: "8px",
                  px: 1.5,
                  "&:hover": { borderColor: "#CBD5E1", backgroundColor: "#F8FAFC" },
                }}
              >
                Export
              </Button>

              <IconButton size="small" onClick={handleFullscreenToggle} sx={{ color: "#64748B" }}>
                <Maximize2 size={18} />
              </IconButton>
            </Stack>
          </Stack>

          {/* Chart Content */}
          <Box sx={{ flexGrow: 1, width: "100%", pt: 1, overflow: "hidden" }}>
            {renderChart(false)}
          </Box>

          <Typography variant="caption" align="center" color="#10B981" fontWeight="600">
            Count
          </Typography>
        </CardContent>
      </Card>

      {/* Fullscreen Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={handleFullscreenToggle}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
            maxHeight: "90vh",
          },
        }}
      >
        <Box sx={{ p: 2.5, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="700" color="#0F172A">
            All Registrations by Country
          </Typography>
          <IconButton onClick={handleFullscreenToggle} sx={{ color: "#64748B" }}>
            <CloseIcon size={20} />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2, pt: 1, overflowY: "auto" }}>
          {renderChart(true)}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CountrywiseRegistrationsCard2;
